import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const SCANNER_ELEMENT_ID = 'tyw-barcode-scanner';

export default function BarcodeScannerDialog({ open, onClose, onScan, title = 'Scan barcode or QR code' }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, { verbose: false });
    scannerRef.current = scanner;

    const startScanner = async () => {
      setIsStarting(true);
      setError('');
      try {
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.75;
              return { width: size, height: size };
            },
            aspectRatio: 1,
          },
          (decodedText) => {
            const value = String(decodedText || '').trim();
            if (!value) return;
            onScan?.(value);
            scanner
              .stop()
              .catch(() => {})
              .finally(() => {
                scannerRef.current = null;
                onClose?.();
              });
          },
          () => {
            /* scan attempt — no match yet */
          }
        );
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Unable to access camera. Check permissions.');
        }
      } finally {
        if (!cancelled) setIsStarting(false);
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {});
      }
      scannerRef.current = null;
    };
  }, [open, onClose, onScan]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <Card className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl">
        <CardContent className="space-y-4 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-base font-semibold text-foreground">
                <Camera className="h-4 w-4" />
                {title}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Point your camera at a device barcode or QR code.
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close scanner">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-black">
            {isStarting && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            )}
            <div id={SCANNER_ELEMENT_ID} className="min-h-[280px] w-full" />
          </div>

          {error ? <p className="text-sm text-red-500">{error}</p> : null}
        </CardContent>
      </Card>
    </div>,
    document.body
  );
}
